import { useCallback, useRef, useEffect } from 'react';
import { useWorkflowFilters, type WorkflowFilters } from './useWorkflowFilters';
import { useWorkflowBatches } from './useWorkflowBatches';
import { useWorkflowStats } from './useWorkflowStats';
import { useWorkflowSelection } from './useWorkflowSelection';
import type { BatchWorkflowListItem, WorkflowDashboardData } from '../../types/workflow';

/**
 * Return type for the useWorkflowDashboard hook.
 * Provides a unified interface to all workflow functionality.
 */
export interface UseWorkflowDashboardReturn {
  // Filters
  /** Current filter state */
  filters: WorkflowFilters;
  /** Set a single filter value */
  setFilter: <K extends keyof WorkflowFilters>(key: K, value: WorkflowFilters[K]) => void;
  /** Set multiple filter values at once */
  setFilters: (filters: Partial<WorkflowFilters>) => void;
  /** Reset all filters to defaults */
  resetFilters: () => void;
  /** Whether any non-default filters are active */
  hasActiveFilters: boolean;
  /** Count of active filters */
  activeFilterCount: number;

  // Data
  /** Array of batch workflow items */
  batches: BatchWorkflowListItem[];
  /** Total count of items matching current filters */
  totalCount: number;
  /** Total number of pages */
  totalPages: number;
  /** Workflow dashboard statistics */
  stats: WorkflowDashboardData | null;

  // Loading/Error states
  /** Whether any data is loading */
  loading: boolean;
  /** Whether batches are loading */
  batchesLoading: boolean;
  /** Whether stats are loading */
  statsLoading: boolean;
  /** Error message if any fetch failed */
  error: string | null;

  // Selection
  /** Set of selected batch IDs */
  selectedIds: Set<number>;
  /** Count of selected items */
  selectedCount: number;
  /** Check if a batch is selected */
  isSelected: (id: number) => boolean;
  /** Toggle selection of a single batch */
  toggleSelection: (id: number) => void;
  /** Select all currently visible batches */
  selectAll: () => void;
  /** Clear all selections */
  clearSelection: () => void;

  // Actions
  /** Refetch both batches and stats */
  refetch: () => void;
}

/**
 * Main orchestration hook that composes all workflow hooks.
 * This is the ONLY hook that container components need to use.
 *
 * Follows the Facade Pattern - provides a simple interface to a complex subsystem
 * of specialized hooks (filters, batches, stats, selection).
 *
 * IMPORTANT: All returned functions are stable (wrapped in useCallback with
 * refs to avoid dependency issues) to prevent unnecessary re-renders in
 * consuming components.
 *
 * @returns Combined interface with all workflow functionality
 *
 * @example
 * ```tsx
 * function WorkflowPage() {
 *   const {
 *     batches,
 *     loading,
 *     error,
 *     filters,
 *     setFilter,
 *     selectedIds,
 *     toggleSelection,
 *     refetch,
 *   } = useWorkflowDashboard();
 *
 *   if (loading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage message={error} />;
 *
 *   return (
 *     <div>
 *       <FilterBar filters={filters} onChange={setFilter} />
 *       <BatchList
 *         batches={batches}
 *         selectedIds={selectedIds}
 *         onToggle={toggleSelection}
 *       />
 *       <Button onClick={refetch}>Refresh</Button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useWorkflowDashboard(): UseWorkflowDashboardReturn {
  // Compose specialized hooks
  const filterHook = useWorkflowFilters();
  const batchesHook = useWorkflowBatches(filterHook.filters);
  const statsHook = useWorkflowStats();
  const selectionHook = useWorkflowSelection();

  // =============================================================================
  // REFS FOR STABLE CALLBACKS
  // =============================================================================
  // Store refetch functions in refs to avoid dependency issues.
  // This allows us to create stable callbacks that always use the latest
  // refetch implementations without triggering re-renders when they change.

  const batchesRefetchRef = useRef(batchesHook.refetch);
  const statsRefetchRef = useRef(statsHook.refetch);
  const batchesRef = useRef(batchesHook.batches);
  const selectAllRef = useRef(selectionHook.selectAll);
  const clearSelectionRef = useRef(selectionHook.clearSelection);

  // Keep refs synchronized with latest values
  useEffect(() => {
    batchesRefetchRef.current = batchesHook.refetch;
  }, [batchesHook.refetch]);

  useEffect(() => {
    statsRefetchRef.current = statsHook.refetch;
  }, [statsHook.refetch]);

  useEffect(() => {
    batchesRef.current = batchesHook.batches;
  }, [batchesHook.batches]);

  useEffect(() => {
    selectAllRef.current = selectionHook.selectAll;
  }, [selectionHook.selectAll]);

  useEffect(() => {
    clearSelectionRef.current = selectionHook.clearSelection;
  }, [selectionHook.clearSelection]);

  // =============================================================================
  // CLEAR SELECTION ON FILTER CHANGE
  // =============================================================================
  // When filters change, the displayed batches change, so any selection
  // from the previous filter state is no longer valid.
  // We use a string key derived from filter values (not the object itself)
  // to avoid unnecessary effect triggers from object identity changes.

  const filterKey = [
    filterHook.filters.statusCode,
    filterHook.filters.priority,
    filterHook.filters.overdueOnly,
    filterHook.filters.search,
    filterHook.filters.sampleType,
    filterHook.filters.datePreset,
    filterHook.filters.dateFrom?.getTime() ?? '',
    filterHook.filters.dateTo?.getTime() ?? '',
  ].join('-');

  useEffect(() => {
    clearSelectionRef.current();
  }, [filterKey]);

  // =============================================================================
  // STABLE ACTIONS
  // =============================================================================

  /**
   * Refetch both batches and stats data.
   * Uses refs internally so the callback identity is stable (empty deps).
   */
  const refetchAll = useCallback(() => {
    batchesRefetchRef.current();
    statsRefetchRef.current();
  }, []); // Empty deps - uses refs

  /**
   * Select all currently visible batches.
   * Uses refs internally so the callback identity is stable.
   */
  const selectAllBatches = useCallback(() => {
    selectAllRef.current(batchesRef.current.map(b => b.id));
  }, []); // Empty deps - uses refs

  // =============================================================================
  // COMBINED RETURN VALUE
  // =============================================================================

  return {
    // Filters - pass through from filter hook
    filters: filterHook.filters,
    setFilter: filterHook.setFilter,
    setFilters: filterHook.setFilters,
    resetFilters: filterHook.resetFilters,
    hasActiveFilters: filterHook.hasActiveFilters,
    activeFilterCount: filterHook.activeFilterCount,

    // Data - pass through from data hooks
    batches: batchesHook.batches,
    totalCount: batchesHook.totalCount,
    totalPages: batchesHook.totalPages,
    stats: statsHook.stats,

    // Loading/Error - combine from data hooks
    loading: batchesHook.loading || statsHook.loading,
    batchesLoading: batchesHook.loading,
    statsLoading: statsHook.loading,
    error: batchesHook.error?.message || statsHook.error || null,

    // Selection - pass through from selection hook (with stable selectAll)
    selectedIds: selectionHook.selectedIds,
    selectedCount: selectionHook.selectedCount,
    isSelected: selectionHook.isSelected,
    toggleSelection: selectionHook.toggle,
    selectAll: selectAllBatches,
    clearSelection: selectionHook.clearSelection,

    // Actions - stable combined actions
    refetch: refetchAll,
  };
}
